import React, { useState, useEffect } from 'react';
import { supabase } from '../supabaseClient';
import { useNavigate } from 'react-router-dom';
import { FaEye, FaEyeSlash, FaEnvelope, FaLock, FaKey } from 'react-icons/fa';
import { motion } from 'framer-motion';
import './Login.css';

function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [resetEmailSent, setResetEmailSent] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [showResetModal, setShowResetModal] = useState(false);
  const navigate = useNavigate();

  const handleLogin = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      const { data: user, error } = await supabase.auth.signInWithPassword({
        email: email,
        password: password,
      });

      if (error) throw error;

      if (user) {
        navigate('/dashboard');
      }
    } catch (error) {
      setError(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleResetPassword = async () => {
    if (!resetEmail) {
      setError("Por favor ingresa un correo electrónico para restablecer la contraseña");
      return;
    }
    
    setIsLoading(true);
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(resetEmail, {
        redirectTo: `https://vacaciones.wsp.com.mx/reset-password`,
      });
      
      if (error) throw error;
      
      setResetEmailSent(true);
      setTimeout(() => {
        setShowResetModal(false);
        setResetEmailSent(false);
      }, 5000);
    } catch (error) {
      setError(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const openResetModal = () => {
    setResetEmail(email); // Pre-fill with login email if available
    setShowResetModal(true);
    setError(null);
  };

  return (
    <div className="login-container">
      <motion.div 
        className="login-card"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <motion.img
          src="/WSP_LOGO-01.jpg"
          alt="WSP Logo"
          className="login-logo"
          initial={{ scale: 0.8 }}
          animate={{ scale: 1 }}
          transition={{ duration: 0.5, delay: 0.2 }}
        />
        
        <motion.h1 
          className="login-title"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5, delay: 0.3 }}
        >
          Iniciar sesión
        </motion.h1>
        
        <motion.p 
          className="login-subtitle"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5, delay: 0.4 }}
        >
          Portal de vacaciones, para uso interno de WSP únicamente.
        </motion.p>

        {error && (
          <motion.div 
            className="login-error-message"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
          >
            {error}
          </motion.div>
        )}

        <motion.form 
          className="login-form" 
          onSubmit={handleLogin}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5, delay: 0.5 }}
        >
          <div className="login-form-group">
            <label className="login-label">
              <FaEnvelope className="login-input-icon" />
              Correo electrónico
            </label>
            <motion.div 
              className="login-input-wrapper"
              whileFocus={{ scale: 1.02 }}
              transition={{ duration: 0.2 }}
            >
              <input
                type="email"
                placeholder="ejemplo.ejemplo@wsp.com.mx"
                className="login-input"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </motion.div>
          </div>

          <div className="login-form-group">
            <label className="login-label">
              <FaLock className="login-input-icon" />
              Contraseña
            </label>
            <div className="login-password-container">
              <input
                type={showPassword ? "text" : "password"}
                placeholder="********"
                className="login-input"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
              <motion.button
                type="button"
                className="login-password-toggle"
                onClick={() => setShowPassword(!showPassword)}
                tabIndex="-1"
                whileTap={{ scale: 0.95 }}
              >
                {showPassword ? <FaEyeSlash /> : <FaEye />}
              </motion.button>
            </div>
          </div>
          
          <motion.button 
            whileHover={{ scale: 1.03 }}
            whileTap={{ scale: 0.98 }}
            type="button" 
            className="login-forgot-password"
            onClick={openResetModal}
            transition={{ duration: 0.2 }}
          >
            <FaKey className="login-forgot-icon" />
            ¿Olvidaste tu contraseña?
          </motion.button>

          <motion.button 
            type="submit" 
            className="login-button"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            disabled={isLoading}
          >
            {isLoading ? (
              <div className="login-button-loader"></div>
            ) : (
              "Iniciar sesión"
            )}
          </motion.button>
        </motion.form>
      </motion.div>

      {/* Password Reset Modal */}
      {showResetModal && (
        <div className="reset-modal-overlay">
          <motion.div 
            className="reset-modal"
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.8 }}
          >
            <h2 className="reset-modal-title">Restablecer contraseña</h2>
            
            {resetEmailSent ? (
              <div className="reset-success-message">
                Se ha enviado un correo electrónico con instrucciones para restablecer tu contraseña.
              </div>
            ) : (
              <>
                <p className="reset-modal-subtitle">
                  Ingresa tu correo electrónico y te enviaremos instrucciones para restablecer tu contraseña.
                </p>
                
                {error && <div className="login-error-message">{error}</div>}
                
                <div className="login-form-group">
                  <label className="login-label">
                    <FaEnvelope className="login-input-icon" />
                    Correo electrónico
                  </label>
                  <input
                    type="email"
                    placeholder="ejemplo.ejemplo@wsp.com.mx"
                    className="login-input"
                    value={resetEmail}
                    onChange={(e) => setResetEmail(e.target.value)}
                  />
                </div>
                
                <div className="reset-modal-buttons">
                  <button 
                    type="button" 
                    className="reset-cancel-button"
                    onClick={() => setShowResetModal(false)}
                    disabled={isLoading}
                  >
                    Cancelar
                  </button>
                  
                  <button 
                    type="button" 
                    className="login-button"
                    onClick={handleResetPassword}
                    disabled={isLoading}
                  >
                    {isLoading ? (
                      <div className="login-button-loader"></div>
                    ) : (
                      "Enviar correo"
                    )}
                  </button>
                </div>
              </>
            )}
          </motion.div>
        </div>
      )}
    </div>
  );
}

export default Login;